import { jsx, jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from "react";
import cx from "classnames";
import SSRIcon from "@ingka/ssr-icon";
const SegmentedControl = (props) => {
  const {
    prefix = "",
    className,
    items,
    onClick = () => void 0,
    activeIndex,
    defaultActiveIndex = 0,
    subtle = false,
    fluid = true,
    size = "medium",
    iconOnly = false,
    disabled = false,
    ...otherProps
  } = props;
  const itemRefs = useRef(items.map(() => null));
  const containerRef = useRef(null);
  const pendingUpdate = React.useRef(
    void 0
  );
  const [isMounted, setIsMounted] = useState(false);
  const [effectiveIndex, setEffectiveIndex] = useState(
    activeIndex !== void 0 ? activeIndex : defaultActiveIndex
  );
  useEffect(() => {
    if (activeIndex !== void 0) {
      setEffectiveIndex(Math.min(activeIndex, items.length - 1));
    } else {
      setEffectiveIndex((prevIndex) => Math.min(prevIndex, items.length - 1));
    }
  }, [activeIndex, items]);
  function shouldUpdateIndicatorPosition() {
    clearTimeout(pendingUpdate.current);
    pendingUpdate.current = setTimeout(updateIndicatorPosition, 35);
  }
  const updateIndicatorPosition = () => {
    const activeItemRef = itemRefs.current[effectiveIndex];
    if (activeItemRef && containerRef.current) {
      const { offsetWidth, offsetLeft } = activeItemRef;
      const { style } = containerRef.current;
      style.setProperty("--highlight-width", `${offsetWidth}px`);
      style.setProperty("--highlight-x-pos", `${offsetLeft}px`);
      if (isMounted) {
        style.setProperty("--highlight-transition-property", "transform");
      }
    }
  };
  useEffect(() => {
    setIsMounted(true);
    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      const observer = new ResizeObserver(shouldUpdateIndicatorPosition);
      observer.observe(containerRef.current);
      return () => {
        observer.disconnect();
      };
    }
    return;
  }, [effectiveIndex, items]);
  const baseClass = `${prefix}segmented-control`;
  const classes = cx(
    baseClass,
    subtle && `${baseClass}--subtle`,
    size && `${baseClass}--${size}`,
    className
  );
  const SegmentButtons = () => {
    return items.map((buttonProps, index) => {
      const { ssrIcon, label, ...other } = buttonProps;
      const isActive = effectiveIndex === index;
      const divider = index > 0 && index < items.length && effectiveIndex !== index && effectiveIndex !== index - 1;
      const buttonClass = `${prefix}segmented-control__button`;
      const buttonClasses = cx(
        buttonClass,
        divider && `${buttonClass}-divider`,
        isActive && `${buttonClass}--active`,
        iconOnly && `${buttonClass}--icon-only`
      );
      return /* @__PURE__ */ jsxs(
        "button",
        {
          ref: (element) => itemRefs.current[index] = element,
          className: buttonClasses,
          disabled,
          "aria-pressed": isActive,
          onClick: (e) => {
            if (!disabled) {
              if (activeIndex === void 0) {
                setEffectiveIndex(index);
              }
              onClick(e, index);
            }
          },
          ...other,
          children: [
            iconOnly && ssrIcon && /* @__PURE__ */ jsx(
              SSRIcon,
              {
                prefix,
                className: `${buttonClass}-icon`,
                paths: ssrIcon
              }
            ),
            label && /* @__PURE__ */ jsx("span", { className: `${buttonClass}-label`, children: label })
          ]
        },
        index
      );
    });
  };
  const innerClass = `${prefix}segmented-control__inner`;
  const innerClasses = cx(
    innerClass,
    fluid && `${innerClass}--fluid`,
    isMounted && `${innerClass}--transition`,
    disabled && `${innerClass}--disabled`
  );
  return /* @__PURE__ */ jsx("div", { className: classes, ref: containerRef, ...otherProps, children: /* @__PURE__ */ jsx("div", { className: innerClasses, children: SegmentButtons() }) });
};
var SegmentedControl_default = SegmentedControl;
export {
  SegmentedControl_default as default
};
